<template>
  <div class="flex flex-col h-full overflow-hidden" :class="classProps" >
    <!--
      AB#21245 — the default search row is a full-width field and a solid black
      button on a line of its own. A page that wants search beside its own
      controls passes a `toolbar` slot and lays it out itself; the search state
      and its commit stay here, so nothing has to re-implement them.
    -->
    <div class="shrink-0" v-if="searchable && $slots.toolbar">
      <slot
        name="toolbar"
        :search-query="searchQuery"
        :set-search="(v: string) => (searchQuery = v)"
        :perform-search="performSearch"
      />
    </div>
    <div class="shrink-0" v-else-if="searchable">
      <div class="flex justify-end items-center py-4">
        <input
          type="text"
          v-model="searchQuery"
          @keyup.enter="performSearch"
          :placeholder="searchPlaceholder ?? 'Search...'"
          class="border p-2 rounded-md w-full min-w-0 sm:min-w-[170px] sm:w-1/3 mr-1 focus:outline-none"
        />
        <button @click="performSearch" class="px-2 py-2 h-full bg-ink text-surface rounded-md">
          <MagnifyingGlassIcon class="size-6" />
        </button>
      </div>
    </div>
    <div
      ref="scroller"
      class="datatable w-full h-max overflow-x-auto bg-surface rounded-lg shadow-md relative"
      :class="{ 'datatable--resizable': resizable }"
      :key="updateKey"
    >
      <div
        v-if="isLoading || isFetching"
        class="absolute top-0 left-0 w-full h-full bg-surface z-10 flex items-center justify-center"
      >
        <div class="flex flex-col items-center gap-2">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-ink"></div>
          <span class="text-ink-soft">{{ isLoading ? 'Loading...' : 'Updating...' }}</span>
        </div>
      </div>
      <div class="datatable-wrapper flex flex-col">
        <div class="flex-1" :key="updateKey">
          <table class="min-w-full border-collapse">
            <!-- Sticky header -->
            <thead
              class="sticky top-0 z-20"
              :class="isCompact ? 'dt-head-neutral' : 'dt-head-comfortable'"
            >
              <tr v-if="validColumns.length > 0">
                <th
                  v-for="col in validColumns"
                  :key="col.key"
                  @click="() => sort(col)"
                  :title="col.title ?? col.label"
                  :class="headerClass(col)"
                  :style="cellStyle(col)"
                  :data-col="colId(col)"
                >
                  <div
                    class="flex items-center gap-1"
                    :class="isCompact ? 'overflow-hidden' : 'text-base'"
                  >
                    <template v-if="col.sortable">
                      <component :is="getSortIcon(col.sortKey)" class="min-w-4 min-h-4 w-4 h-4" />
                    </template>
                    <span :class="isCompact ? 'truncate' : ''">{{ col.label }}</span>
                  </div>
                  <!--
                    AB#21576 — the drag handle on a header's right edge. Drag to
                    set the width, double-click to fit the widest visible cell.
                    It swallows its own clicks so a drag never doubles as a sort.
                  -->
                  <span
                    v-if="resizable"
                    class="dt-rz"
                    :class="{ 'dt-rz--on': resizing === colId(col) }"
                    role="separator"
                    aria-orientation="vertical"
                    :aria-label="`Resize ${col.label}`"
                    :title="`Drag to resize, double-click to fit`"
                    data-test="dt-resize-handle"
                    @click.stop
                    @dblclick.stop.prevent="autoFit(col)"
                    @pointerdown.stop.prevent="startResize($event, col)"
                    @pointermove="moveResize"
                    @pointerup="endResize"
                    @pointercancel="endResize"
                  ></span>
                </th>
                <!--
                  AB#21576 — once any column has been sized, an empty column
                  takes whatever room is left. Without it the browser hands the
                  slack to the other columns, and shrinking one column widens
                  its neighbours, which is not what anyone dragging expects.
                -->
                <th v-if="fillerColumn" class="dt-filler" aria-hidden="true"></th>
                <!--
                  AB#21242 — at compact density the Actions header is dropped and
                  the column narrowed to the width of the kebab. A column of
                  identical ⋮ glyphs needs no label to be understood, and the
                  word was the widest thing in the column, so it was setting the
                  width of a column that holds a 24px control.
                -->
                <th
                  v-if="showActionsColumn"
                  :class="[
                    isCompact
                      ? 'h-[36px] w-[52px] px-0 text-center'
                      : 'p-3 text-left font-semibold text-ink-soft',
                    actionsCellClass,
                  ]"
                >
                  <span v-if="!isCompact">Actions</span>
                  <span v-else class="sr-only">Actions</span>
                </th>
              </tr>
              <tr v-else>
                <th colspan="100%" class="text-center p-3">No columns available</th>
              </tr>
            </thead>

            <!-- Scrollable body -->
            <tbody>
              <tr v-if="isLoading && !isFetching">
                <td
                  class="p-3 text-center"
                  :colspan="spanAll"
                >
                  <slot name="loading">
                    <div class="flex justify-center items-center p-3">
                      <div
                        class="animate-spin rounded-full h-6 w-6 border-b-2 border-ink"
                      ></div>
                      <span class="ml-2 text-ink-soft">Loading...</span>
                    </div>
                  </slot>
                </td>
              </tr>

              <tr v-else-if="error">
                <td
                  class="p-3 text-center text-red-600"
                  :colspan="spanAll"
                >
                  <slot name="error">
                    <span>Error loading data. Please try again.</span>
                  </slot>
                </td>
              </tr>

              <template v-else-if="showPagination && paginatedData && paginatedData.length > 0">
                <tr
                  v-for="row in paginatedData"
                  :key="(row as any).id"
                  class="border-t hover:bg-surface-2 cursor-pointer"
                  :data-row-id="row_id ? row[row_id] : ''"
                  @contextmenu.prevent="handleRowRightClick($event, row)"
                >
                  <td
                    v-for="col in validColumns"
                    :key="col.key"
                    @click="(event) => handleCellClick(event, row, col)"
                    :class="[bodyCellClass(col), col.cellClass ? col.cellClass(row) : '']"
                    :style="cellStyle(col)"
                    :data-col="colId(col)"
                  >
                    <span class="ripple"></span>
                    <component
                      :is="getRenderer(col.renderer, row)"
                      v-if="typeof col.renderer === 'function'"
                    />
                    <component
                      :is="getRenderer(col.renderer, row)"
                      v-else-if="typeof col.renderer === 'string'"
                    />
                    <span v-else>{{ (row as any)[col.key] }}</span>
                  </td>

                  <td v-if="fillerColumn" :class="fillerCellClass" aria-hidden="true"></td>
                  <td
                    v-if="showActionsColumn && row"
                    :class="[
                      isCompact ? 'h-[44px] w-[52px] px-0 align-middle text-center' : 'p-3',
                      actionsCellClass,
                      rowClass ? rowClass(row) : '',
                    ]"
                  >
                    <div :class="isCompact ? 'flex items-center justify-center' : ''">
                      <slot name="actions" :row="row"></slot>
                    </div>
                  </td>
                </tr>
              </template>

              <template v-else-if="!showPagination && rawData && rawData.length > 0">
                <tr
                  v-for="row in rawData"
                  :key="(row as any).id"
                  class="border-t hover:bg-surface-2 cursor-pointer"
                >
                  <td
                    v-for="col in validColumns"
                    :key="col.key"
                    class="p-3 text-ink-soft font-[500] text-sm border-line border-[1px] relative overflow-hidden"
                    :style="cellStyle(col)"
                    :data-col="colId(col)"
                    @click="(event) => handleCellClick(event, row, col)"
                  >
                    <span class="ripple"></span>
                    <component
                      :is="getRenderer(col.renderer, row)"
                      v-if="typeof col.renderer === 'function'"
                    />
                    <component
                      :is="getRenderer(col.renderer, row)"
                      v-else-if="typeof col.renderer === 'string'"
                    />
                    <span v-else>{{ (row as any)[col.key] }}</span>
                  </td>

                  <td v-if="fillerColumn" :class="fillerCellClass" aria-hidden="true"></td>
                  <td
                    v-if="showActionsColumn && row"
                    class="p-3"
                    :class="rowClass ? rowClass(row) : ''"
                  >
                    <slot name="actions" :row="row"></slot>
                  </td>
                </tr>
              </template>

              <tr v-else class="border-t hover:bg-surface-2">
                <td
                  class="p-3 text-ink-soft text-center text-sm"
                  :colspan="spanAll"
                >
                  No data to display
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div class="shrink-0">
      <div
        :key="updateKey"
        class="flex justify-center md:justify-between items-center mt-6 px-2 md:px-4 py-3 bg-surface-2 rounded-lg flex-wrap gap-2"
        :style="footerStyle"
        v-if="paginatedData && paginatedData.length && showPagination"
      >
        <!-- AB#21245 — "Showing 1 to 10 of 17819 records" is a sentence where a
             range would do, and an unseparated five-figure number is hard to
             read at a glance. -->
        <span class="text-sm text-ink-soft tabular-nums">
          <template v-if="isCompact">
            {{ nf(startIndex + 1) }}–{{ nf(endIndex) }} of {{ nf(totalItems ?? 0) }}
          </template>
          <template v-else>
            Showing {{ startIndex + 1 }} to {{ endIndex }} of {{ totalItems }} records
          </template>
          <span v-if="isFetching" class="ml-2 text-ink-mute">(Refreshing...)</span>
        </span>
        <!--
          AB#21240 — range pagination. Numbered buttons can only offer the pages
          next to the one you are on, so "go to page 900 of 1,782" was not
          reachable at all. A typed field can offer any page; first/last replace
          the "..." that was standing in for them.
        -->
        <div
          v-if="useRangePagination"
          class="flex items-center justify-center gap-2 flex-wrap dt-range"
        >
          <button
            @click="changePage(1)"
            :disabled="currentPage <= 1 || isLoading || isFetching"
            aria-label="First page"
            title="First page"
            class="px-2 py-1.5 text-sm border rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
          >
            «
          </button>
          <button
            @click="changePage(currentPage - 1)"
            :disabled="currentPage <= 1 || isLoading || isFetching"
            aria-label="Previous page"
            title="Previous page"
            class="px-2 py-1.5 text-sm border rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ‹
          </button>
          <label class="flex items-center gap-1.5 text-sm text-ink-soft">
            Page
            <input
              v-model="pageInput"
              type="number"
              min="1"
              :max="totalPages || 1"
              :disabled="isLoading || isFetching"
              aria-label="Go to page"
              class="w-16 border rounded-md px-2 py-1 text-sm text-center"
              @keyup.enter="commitPageInput"
              @blur="commitPageInput"
            />
            of {{ totalPages || 1 }}
          </label>
          <button
            @click="changePage(currentPage + 1)"
            :disabled="currentPage >= totalPages || isLoading || isFetching"
            aria-label="Next page"
            title="Next page"
            class="px-2 py-1.5 text-sm border rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ›
          </button>
          <button
            @click="changePage(totalPages)"
            :disabled="currentPage >= totalPages || isLoading || isFetching"
            aria-label="Last page"
            title="Last page"
            class="px-2 py-1.5 text-sm border rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
          >
            »
          </button>
          <div v-if="isFetching" class="flex items-center ml-2">
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-ink"></div>
          </div>
        </div>

        <div v-else class="flex items-center justify-center gap-2 md:gap-3 flex-wrap">
          <button
            v-if="currentPage > 1"
            @click="changePage(1)"
            :disabled="isLoading || isFetching"
            :class="{ 'opacity-50 cursor-not-allowed': isLoading || isFetching }"
            class="p-2 text-sm border rounded-md bg-ink text-surface"
          >
            <ArrowLeftIcon class="w-4 h-4" />
          </button>
          <button
            v-if="currentPage > 1"
            @click="changePage(currentPage - 1)"
            :disabled="isLoading || isFetching"
            :class="{ 'opacity-50 cursor-not-allowed': isLoading || isFetching }"
            class="p-2 text-sm border rounded-md bg-ink text-surface"
          >
            Prev
          </button>
          <button v-if="shouldShowEllipsisBefore" class="p-2 text-ink-mute cursor-default">
            ...
          </button>
          <button
            v-for="n in visiblePages"
            :key="n"
            @click="() => changePage(n)"
            :disabled="isLoading || isFetching"
            :class="{
              'font-bold bg-ink text-surface': n === currentPage,
              'text-ink': n !== currentPage,
              'opacity-50 cursor-not-allowed': isLoading || isFetching,
            }"
            class="px-3 py-1.5 md:px-4 md:py-2 text-sm border border-line rounded-md"
          >
            {{ n }}
          </button>
          <button v-if="shouldShowEllipsisAfter" class="p-2 text-ink-mute cursor-default">
            ...
          </button>
          <button
            v-if="currentPage < totalPages"
            @click="changePage(currentPage + 1)"
            :disabled="isLoading || isFetching"
            :class="{ 'opacity-50 cursor-not-allowed': isLoading || isFetching }"
            class="p-2 text-sm border rounded-md bg-ink text-surface"
          >
            Next
          </button>
          <button
            v-if="currentPage < totalPages"
            @click="changePage(totalPages)"
            :disabled="isLoading || isFetching"
            :class="{ 'opacity-50 cursor-not-allowed': isLoading || isFetching }"
            class="p-2 text-sm border rounded-md bg-ink text-surface"
          >
            <ArrowRightIcon class="w-4 h-4" />
          </button>
          <div v-if="isFetching" class="flex items-center ml-2">
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-ink"></div>
          </div>
        </div>
        <label v-if="isCompact" class="text-sm text-ink-soft mr-1">Rows</label>
        <select
          v-model="pageSize"
          @change="updatePageSize"
          :disabled="isLoading || isFetching"
          :class="{ 'opacity-50 cursor-not-allowed': isLoading || isFetching }"
          class="border border-line p-2 rounded-md bg-surface text-ink-soft"
        >
          <option v-for="size in pageSizeOptions" :key="size" :value="size">
            {{ size }}
          </option>
        </select>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { DashboardRequest } from '@/models';
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline';
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowsUpDownIcon,
  ArrowUpIcon,
} from '@heroicons/vue/24/solid';

import { computed, defineEmits, defineProps, PropType, ref, VNode, watch } from 'vue';
import {
  clampColumnWidth,
  MAX_COLUMN_PX,
  MIN_COLUMN_PX,
  PINNED_MAX_FRACTION,
  type ColumnWidths,
} from '@/utils/tablePreferences';

interface Column {
  key: string;
  label: string;
  sortable?: boolean;
  sortKey?: string;
  cellClass?: (row: any) => string;
  renderer?: (value: any, row: any) => string | number | VNode;
  /**
   * Full text for a header whose label had to be shortened to stay on one line.
   * Falls back to `label`, so every header gets a tooltip and none of them lie.
   */
  title?: string;
  /** Pin this column to an edge while the rest of the table scrolls sideways. */
  sticky?: 'left' | 'right';
  /** Columns carrying the row's identity or its actions cannot be hidden. */
  alwaysVisible?: boolean;
  /**
   * This cell holds a control rather than text, so it must not be clipped.
   * Clipping a centred pill or a fixed-width select cuts it at BOTH edges
   * instead of giving it an ellipsis.
   */
  noTruncate?: boolean;
  /**
   * Replaces the default truncation width for this column, e.g. `max-w-[260px]`.
   * For columns whose value is the thing people read the row for.
   */
  widthClass?: string;
}

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  columns: Array as () => Column[],
  pageSizeOptions: {
    type: Array as () => number[],
    default: () => [5, 10, 20, 50],
  },
  isAsync: Boolean,
  totalItems: Number,
  showActionsColumn: Boolean,
  currentPaginationInfo: {
    type: Object as () => DashboardRequest,
    default: () => ({}) as DashboardRequest,
  },
  searchable: {
    type: Boolean,
    default: false,
  },
  searchPlaceholder: {
    type: String,
    default: 'false',
    required: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isFetching: {
    type: Boolean,
    default: false,
  },
  error: {
    type: [Error, String, null],
    default: null,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  showPagination: {
    type: Boolean,
    default: true,
  },
  row_id: {
    type: String,
    default: undefined,
  },
  rowClass: {
    type: Function as PropType<(row: any) => string>,
    default: () => (row: any) => '',
  },
  updateKey: {
    type: String,
    default: undefined,
  },
  classProps: {
    type: String,
    default: '',
    required: false,
  },
  /**
   * AB#21240 — everything below is opt-in and defaults to the behaviour this
   * table had before. Twenty-three pages render this component; only the orders
   * dashboard asks for the new treatment, so a revert of that one page is a
   * full revert of the redesign.
   *
   * 'compact' gives fixed 44px rows, single-line cells that truncate with an
   * ellipsis, and a quieter uppercase header.
   */
  density: {
    type: String as PropType<'comfortable' | 'compact'>,
    default: 'comfortable',
  },
  /** Pin columns that declare `sticky` to the table's edges. */
  pinnedColumns: {
    type: Boolean,
    default: false,
  },
  /**
   * 'range' replaces the numbered page buttons with first / prev / a typed page
   * field / next / last. Numbered buttons cannot offer page 900 of 1,782 at all.
   */
  paginationStyle: {
    type: String as PropType<'numbered' | 'range'>,
    default: 'numbered',
  },
  /**
   * Right-hand padding reserved in the pagination bar, in pixels. The support
   * widget is a fixed 56px bubble in the bottom-right corner and currently sits
   * on top of the page-size select; space for it is reserved, not hoped for.
   */
  footerGutter: {
    type: Number,
    default: 0,
  },
  /**
   * AB#21576 — a drag handle on every header. Widths are held here while
   * dragging and handed back through `update:columnWidths` when the drag ends,
   * so the page can remember them (useColumnWidths). Off by default: the
   * legacy pages keep their fixed layout.
   */
  resizable: {
    type: Boolean,
    default: false,
  },
  /** Widths in pixels keyed by column `key`; only the columns a person has sized. */
  columnWidths: {
    type: Object as PropType<ColumnWidths>,
    default: () => ({}),
  },
});

const emit = defineEmits<{
  (e: 'fetch-data', data: any): void;
  (e: 'row-click', row: any): void;
  (e: 'cell-click', data: { row: any; column: Column }): void;
  (e: 'row-right-click', payload: { event: MouseEvent; row: any }): void;
  (e: 'update:columnWidths', widths: ColumnWidths): void;
}>();

const currentPage = ref(props.currentPaginationInfo ? props.currentPaginationInfo.page : 1);
const pageSize = ref(props.currentPaginationInfo ? props.currentPaginationInfo.per_page : 10);
const sortColumn = ref<string | null>(
  props.currentPaginationInfo ? props.currentPaginationInfo.sort_by : null
);
const sortOrder = ref<'asc' | 'desc'>(
  props.currentPaginationInfo ? props.currentPaginationInfo.sort_order : 'asc'
);

const handleRowRightClick = (event: MouseEvent, row: any) => {
  event.stopPropagation();
  emit('row-right-click', { event, row });
};

const searchQuery = ref(props.currentPaginationInfo ? props.currentPaginationInfo.search : '');

watch(
  () => props.currentPaginationInfo,
  (newPaginationInfo) => {
    if (!newPaginationInfo) return;
    currentPage.value = newPaginationInfo.page ?? 1;
    pageSize.value = newPaginationInfo.per_page ?? pageSize.value;
    sortColumn.value = newPaginationInfo.sort_by ?? sortColumn.value;
    sortOrder.value = newPaginationInfo.sort_order ?? sortOrder.value;
    searchQuery.value = newPaginationInfo.search ? newPaginationInfo.search : searchQuery.value;
  },
  { deep: true, immediate: true }
);

const shouldShowEllipsisBefore = computed(() => currentPage.value > 7);
const shouldShowEllipsisAfter = computed(() => currentPage.value < totalPages.value - 7);

watch(
  () => props.totalItems as number,
  (newTotal) => {
    const totalPages = Math.ceil((newTotal || 0) / (pageSize.value || 1));

    if (currentPage.value > totalPages) {
      changePage(Math.max(1, totalPages));
    }
  }
);

const performSearch = () => {
  emit('fetch-data', {
    searchQuery: searchQuery.value,
    page: 1,
    pageSize: pageSize.value,
    sortColumn: sortColumn.value,
    sortOrder: sortOrder.value,
  });
};
const validColumns = computed<Column[]>(() => {
  return Array.isArray(props.columns)
    ? props.columns.filter((column): column is Column => !!column)
    : [];
});
/* ---------------------------------------------------------------- *
 * AB#21240 — density, pinning and range pagination.
 * Every helper below returns the previous value when the matching prop is left
 * at its default, so the twenty-two pages that do not opt in render identically.
 * ---------------------------------------------------------------- */

const isCompact = computed(() => props.density === 'compact');

/** Thousands separators, so 17816 reads as a quantity rather than a code. */
const nf = (n: number) => (n ?? 0).toLocaleString('en-US');

/**
 * Truncation classes.
 *
 * `max-width` is what makes `text-overflow` work at all — without it the cell
 * simply widens and the ellipsis never appears. But a cell is only safe to clip
 * when it holds *text*: a column whose cell holds a control (a status pill
 * centred with `mx-auto`, a select with its own `min-width`) gets cut on both
 * edges instead of gaining an ellipsis, which is how "Submitted for Factory
 * Review" rendered as "ubmitted for Factor". Those columns declare
 * `noTruncate` and size to their content; the table scrolls, which is what the
 * pinned edges are for.
 */
const TRUNCATE = 'overflow-hidden text-ellipsis whitespace-nowrap';
const DEFAULT_WIDTH = 'max-w-[200px]';

/** Truncation classes for one column, honouring its own width if it sets one. */
const truncateClass = (col: Column) =>
  !isCompact.value || col.noTruncate ? '' : `${TRUNCATE} ${col.widthClass ?? DEFAULT_WIDTH}`;

/** Header cell classes. */
const headerClass = (col: Column) => {
  const base = isCompact.value
    ? 'h-[36px] px-3 text-[11px] font-bold uppercase tracking-[0.05em] text-ink whitespace-nowrap'
    : 'p-3 text-ink text-base font-semibold border-r-[1px] border-line last-of-type:border-r-0';
  return [
    base,
    truncateClass(col),
    col.sortable ? 'cursor-pointer' : '',
    stickyClass(col),
    // The handle is positioned against the header; a pinned header is already positioned.
    props.resizable && !stickyClass(col) ? 'relative' : '',
  ]
    .filter(Boolean)
    .join(' ');
};

/** Body cell classes, excluding the caller's own `cellClass`. */
const bodyCellClass = (col: Column) => {
  const base = isCompact.value
    ? 'h-[44px] px-3 align-middle text-ink-soft font-[500] text-[13px] border-b border-line relative'
    : 'p-3 text-ink-soft font-[500] text-sm border-line border-[1px] relative overflow-hidden';
  return [base, truncateClass(col), stickyClass(col)].filter(Boolean).join(' ');
};

/**
 * A pinned cell needs its own background: the rows it slides over would
 * otherwise show through, because `position: sticky` does not create one.
 */
const stickyClass = (col: Column) => {
  if (!props.pinnedColumns || !col?.sticky) return '';
  return col.sticky === 'left'
    ? 'sticky left-0 z-[15] dt-pin dt-pin--left'
    : 'sticky right-0 z-[15] dt-pin dt-pin--right';
};

/** The trailing Actions column is pinned right whenever pinning is on. */
const actionsCellClass = computed(() =>
  props.pinnedColumns ? 'sticky right-0 z-[15] dt-pin dt-pin--right' : ''
);

/* ---------------------------------------------------------------- *
 * AB#21576 — resizable columns.
 *
 * The table keeps its automatic layout; a sized column gets an explicit
 * width / min-width / max-width on every one of its cells, which is what makes
 * a width hold in auto layout, and `overflow: hidden` so the cell clips rather
 * than pushes. Columns nobody has sized keep behaving exactly as before, so a
 * page that never sees a drag renders identically to today.
 * ---------------------------------------------------------------- */

const scroller = ref<HTMLElement | null>(null);

/** Live widths: the prop, plus whatever the current drag is doing. */
const widths = ref<ColumnWidths>({ ...(props.columnWidths ?? {}) });

watch(
  () => props.columnWidths,
  (next) => {
    widths.value = { ...(next ?? {}) };
  },
  { deep: true }
);

/** The column being dragged right now, for the handle's highlight. */
const resizing = ref<string | null>(null);

/** The filler column exists as soon as any width has been chosen. */
const fillerColumn = computed(
  () => props.resizable && Object.keys(widths.value).length > 0
);

const fillerCellClass = computed(() =>
  isCompact.value
    ? 'dt-filler p-0 border-b border-line'
    : 'dt-filler p-0 border-line border-[1px]'
);

/** Columns a full-width message row has to span: data, filler, actions. */
const spanAll = computed(
  () =>
    validColumns.value.length + (fillerColumn.value ? 1 : 0) + (props.showActionsColumn ? 1 : 0)
);

/**
 * Excel's rule: dragging one column moves that column and nothing else. In an
 * automatic table layout the browser would re-share the table's width among
 * the unsized columns after every drag, so before the first drag every
 * visible column is pinned at the width it has right now. The pinned widths
 * are remembered with the dragged one, which is also what keeps the grid
 * looking the same after a reload.
 */
const freezeVisibleWidths = () => {
  const root = scroller.value;
  if (!root) return;
  const next = { ...widths.value };
  let changed = false;
  for (const th of Array.from(root.querySelectorAll<HTMLElement>('thead th[data-col]'))) {
    const key = th.dataset.col as string;
    if (next[key]) continue;
    const w = th.getBoundingClientRect().width;
    if (w > 0) {
      next[key] = clampColumnWidth(w);
      changed = true;
    }
  }
  if (changed) widths.value = next;
};

let drag: { key: string; startX: number; startW: number; max: number } | null = null;

/**
 * A drag starts with pointerdown on the handle and ends with pointerup wherever
 * the pointer is; the browser then fires `click` on their common ancestor,
 * which is the header, which would sort. The flag lasts one macrotask.
 */
let suppressSort = false;

/**
 * The name a column's width is stored under. Several dashboard columns are
 * renderer-only and used to ship with `key: ''`; six of them sharing one empty
 * name meant none of them could be sized. The key is the identity; the sort key
 * and then the label stand in when a caller left it blank.
 */
const colId = (col: Column): string => col.key || col.sortKey || col.label;

const cellStyle = (col: Column) => {
  if (!props.resizable) return undefined;
  const w = widths.value[colId(col)];
  if (!w) return undefined;
  const px = `${w}px`;
  return { width: px, minWidth: px, maxWidth: px, overflow: 'hidden' };
};

/**
 * A pinned column is capped at half the grid: it sits on top of everything
 * that scrolls, so past that point it would hide what people scroll to see.
 */
const maxWidthFor = (col: Column): number => {
  if (props.pinnedColumns && col.sticky) {
    const box = scroller.value?.clientWidth ?? 0;
    return box > 0 ? Math.max(MIN_COLUMN_PX, Math.floor(box * PINNED_MAX_FRACTION)) : MAX_COLUMN_PX;
  }
  return MAX_COLUMN_PX;
};

const commitWidths = () => {
  emit('update:columnWidths', { ...widths.value });
};

const startResize = (event: PointerEvent, col: Column) => {
  if (!props.resizable || (event.button !== 0 && event.pointerType === 'mouse')) return;
  const handle = event.currentTarget as HTMLElement;
  const th = handle.closest('th');
  freezeVisibleWidths();
  const id = colId(col);
  const startW = widths.value[id] ?? th?.getBoundingClientRect().width ?? MIN_COLUMN_PX;
  drag = { key: id, startX: event.clientX, startW, max: maxWidthFor(col) };
  resizing.value = id;
  try {
    handle.setPointerCapture(event.pointerId);
  } catch {
    /* jsdom and very old browsers: the handle still receives the events. */
  }
  document.body.classList.add('dt-resizing');
};

const moveResize = (event: PointerEvent) => {
  if (!drag) return;
  const next = clampColumnWidth(drag.startW + (event.clientX - drag.startX), MIN_COLUMN_PX, drag.max);
  if (widths.value[drag.key] !== next) {
    widths.value = { ...widths.value, [drag.key]: next };
  }
};

const endResize = (event: PointerEvent) => {
  if (!drag) return;
  const handle = event.currentTarget as HTMLElement | null;
  try {
    handle?.releasePointerCapture(event.pointerId);
  } catch {
    /* Not captured; nothing to release. */
  }
  const changed = JSON.stringify(widths.value) !== JSON.stringify(props.columnWidths ?? {});
  drag = null;
  resizing.value = null;
  document.body.classList.remove('dt-resizing');
  suppressSort = true;
  setTimeout(() => {
    suppressSort = false;
  }, 0);
  if (changed) commitWidths();
};

/**
 * The width a cell wants: its widest child plus its own padding. Children are
 * measured rather than the cell because a clipped cell reports its clip, not
 * its content; an inline span reports its full text even when the cell cuts it.
 */
const naturalCellWidth = (cell: HTMLElement): number => {
  const cs = getComputedStyle(cell);
  const pad = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
  let content = 0;
  for (const child of Array.from(cell.children) as HTMLElement[]) {
    if (child.classList.contains('ripple') || child.classList.contains('dt-rz')) continue;
    content = Math.max(content, child.scrollWidth, child.getBoundingClientRect().width);
  }
  return content > 0 ? Math.ceil(content + pad + 2) : 0;
};

/** Double-click: fit the column to the widest cell on this page of rows. */
const autoFit = (col: Column) => {
  const root = scroller.value;
  if (!root || !props.resizable) return;
  freezeVisibleWidths();
  const cells = Array.from(
    root.querySelectorAll<HTMLElement>(`thead th[data-col="${colId(col)}"], tbody td[data-col="${colId(col)}"]`)
  ).filter((cell) => (cell as HTMLTableCellElement).colSpan <= 1);
  let best = 0;
  for (const cell of cells) best = Math.max(best, naturalCellWidth(cell));
  if (best <= 0) return;
  const next = clampColumnWidth(best, MIN_COLUMN_PX, maxWidthFor(col));
  if (widths.value[colId(col)] !== next) widths.value = { ...widths.value, [colId(col)]: next };
  if (JSON.stringify(widths.value) !== JSON.stringify(props.columnWidths ?? {})) commitWidths();
};

const useRangePagination = computed(() => props.paginationStyle === 'range');

const footerStyle = computed(() =>
  props.footerGutter > 0 ? { paddingRight: `${props.footerGutter}px` } : undefined
);

/** Typed page field. Committed on Enter or blur, never per keystroke. */
const pageInput = ref(String(props.currentPage ?? 1));

watch(
  () => currentPage.value,
  (page) => {
    pageInput.value = String(page);
  }
);

const commitPageInput = () => {
  const parsed = Number.parseInt(pageInput.value, 10);
  if (!Number.isFinite(parsed)) {
    pageInput.value = String(currentPage.value);
    return;
  }
  const clamped = Math.min(Math.max(1, parsed), Math.max(1, totalPages.value));
  pageInput.value = String(clamped);
  if (clamped !== currentPage.value) changePage(clamped);
};

const sortedData = computed(() => {
  if (!sortColumn.value) return props.data;
  return props.data
    ? [...props.data].sort((a: any, b: any) => {
        const result = a[sortColumn.value!] > b[sortColumn.value!] ? 1 : -1;
        return sortOrder.value === 'asc' ? result : -result;
      })
    : [];
});

const paginatedData = computed(() => {
  if (props.isAsync) return props.data;
  const start = (currentPage.value - 1) * pageSize.value;
  return sortedData.value?.slice(start, start + pageSize.value);
});

const rawData = computed(() => props.data ?? []);

const totalRecords = computed(() => props.totalItems ?? 0);
const totalPages = computed(() => Math.ceil(totalRecords.value / pageSize.value));
const visiblePages = computed(() => {
  const start = Math.max(1, currentPage.value - 3);
  const end = Math.min(totalPages.value, start + 6);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

const startIndex = computed(() => {
  if (totalRecords.value === 0) return 0;
  return (currentPage.value - 1) * pageSize.value;
});
const endIndex = computed(() => {
  if (totalRecords.value === 0) return 0;
  return Math.min(startIndex.value + pageSize.value, totalRecords.value);
});

const sort = (col: Column) => {
  if (!col.sortable || suppressSort) return;
  sortOrder.value = sortColumn.value === col.sortKey && sortOrder.value === 'asc' ? 'desc' : 'asc';
  sortColumn.value = col.sortKey ?? null;
  if (props.isAsync) {
    emit('fetch-data', {
      sortColumn: sortColumn.value,
      sortOrder: sortOrder.value,
      pageSize: pageSize.value,
      page: currentPage.value,
      searchQuery: searchQuery.value,
    });
  }
};
const getRenderer = (renderer: any, row: any) => {
  if (!renderer) return null;

  if (typeof renderer === 'function') {
    return () => renderer(null, row); // Ensure renderer is properly executed
  }

  return renderer;
};
const getSortIcon = (key: string | undefined) => {
  if (sortColumn.value === key) {
    return sortOrder.value === 'asc' ? ArrowUpIcon : ArrowDownIcon;
  }
  return ArrowsUpDownIcon;
};

const changePage = (page: number) => {
  // Don't allow page changes while loading
  if (props.isLoading || props.isFetching) return;

  // Make sure we don't try to navigate to a page that doesn't exist
  const validPage = Math.min(page, totalPages.value || 1);
  currentPage.value = validPage > 0 ? validPage : 1;

  if (props.isAsync) {
    emit('fetch-data', {
      page: currentPage.value,
      pageSize: pageSize.value,
      sortColumn: sortColumn.value,
      sortOrder: sortOrder.value,
      searchQuery: searchQuery.value,
    });
  }
};

const updatePageSize = () => {
  // Don't allow page size changes while loading
  if (props.isLoading || props.isFetching) return;

  currentPage.value = 1;
  emit('fetch-data', {
    page: currentPage.value,
    pageSize: pageSize.value,
    sortColumn: sortColumn.value,
    sortOrder: sortOrder.value,
    searchQuery: searchQuery.value,
  });
};

const handleCellClick = (event: MouseEvent, row: any, col: Column) => {
  // Check if the click was on a button or interactive element
  const target = event.target as HTMLElement;
  const isButtonClick =
    target.closest('button') !== null ||
    target.closest('select') !== null ||
    target.closest('a') !== null ||
    target.closest('[role="button"]') !== null;

  if (isButtonClick) {
    // Let the button handle its own click
    return;
  }

  // For non-button clicks, emit the cell-click event
  emit('cell-click', { row, column: col });

  // Ripple effect
  const cell = event.currentTarget as HTMLElement;
  const ripple = cell.querySelector('.ripple') as HTMLElement;
  if (ripple) {
    ripple.classList.remove('show');
    // Get click coordinates relative to the cell
    const rect = cell.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    ripple.style.width = ripple.style.height = size + 'px';
    ripple.style.left = event.clientX - rect.left - size / 2 + 'px';
    ripple.style.top = event.clientY - rect.top - size / 2 + 'px';
    // Trigger reflow to restart animation
    void ripple.offsetWidth;
    ripple.classList.add('show');
  }
};
</script>

<style scoped lang="scss">
thead th {
  position: sticky;
  top: 0;
  z-index: 10;
  /* Neutral fallback; each density restates its own below so both themes hold. */
  background-color: var(--surface-2);
}
/* AB#21596 — the comfortable header keeps its soft-accent tint, now token-based
   so it re-mixes for dark instead of staying pale blue on a dark ground. */
.dt-head-comfortable th {
  background-color: var(--accent-soft);
}

/*
  AB#21240 — pinned columns.

  `position: sticky` creates no background of its own, so a pinned cell would
  let the rows it slides over show through. The fix is a concrete background on
  the row and `inherit` on the pinned cell: the cell then picks up whatever the
  row is, and a cell that sets its own colour — the urgency tints and the
  Shop-in-Shop fill both use `!bg-...` — still wins on the cell itself. Painting
  the pinned cell white directly would have erased exactly those cues, since the
  pinned column is the one that carries them.
*/
tbody tr {
  background-color: var(--surface);
}
.dt-pin {
  background-color: inherit;
}
.dt-pin--left {
  box-shadow: 1px 0 0 var(--border);
}
.dt-pin--right {
  box-shadow: -1px 0 0 var(--border);
}
/*
  Where the sticky header meets a pinned column the header must win, and it must
  be opaque. `.dt-pin`'s `inherit` outranks the `thead th` rule by specificity,
  and a `<tr>` has no background of its own, so the pinned header resolved to
  transparent and the columns scrolling underneath showed straight through it.
  The colour is restated here rather than dropping `inherit`, which the body
  cells rely on to keep their row tints.
*/
thead .dt-pin {
  z-index: 25;
  background-color: var(--surface-2);
}
.dt-head-comfortable .dt-pin {
  background-color: var(--accent-soft);
}

/* AB#21245 — a lighter header lets the rows carry the page; the weight and the
   ink do the separating instead of a saturated fill. */
.dt-head-neutral th {
  background-color: var(--surface-2);
  border-bottom: 1px solid var(--border);
}
.dt-head-neutral .dt-pin {
  background-color: var(--surface-2);
}

/* The typed page field: drop the spinners, which are useless at this size. */
.dt-range input[type='number'] {
  appearance: textfield;
  -moz-appearance: textfield;
}
.dt-range input[type='number']::-webkit-outer-spin-button,
.dt-range input[type='number']::-webkit-inner-spin-button {
  appearance: none;
  margin: 0;
}

/*
  AB#21576 — the resize handle. 8px wide so a mouse can find it, drawn as a
  2px line only while the header is hovered or the column is being dragged.
*/
.dt-rz {
  position: absolute;
  top: 0;
  right: 0;
  width: 8px;
  height: 100%;
  cursor: col-resize;
  z-index: 3;
  touch-action: none;
  user-select: none;
}
.dt-rz::after {
  content: '';
  position: absolute;
  top: 8px;
  bottom: 8px;
  left: 3px;
  width: 2px;
  border-radius: 2px;
  background: transparent;
}
th:hover .dt-rz::after {
  background: var(--border);
}
/* Same specificity as the hover rule above, declared after it, so it wins. */
th .dt-rz:hover::after,
th .dt-rz--on::after {
  background: var(--accent);
}
/* The filler column: no content, no handle, takes the slack, draws nothing. */
.dt-filler {
  width: auto;
  min-width: 0;
}
/* While a drag is on, nothing else may take the cursor or start selecting text. */
body.dt-resizing,
body.dt-resizing * {
  cursor: col-resize !important;
  user-select: none !important;
}

.ripple {
  position: absolute;
  border-radius: 50%;
  transform: scale(0);
  opacity: 0.75;
  pointer-events: none;
  background-color: rgba(185, 185, 185, 0.3);
  z-index: 2;
  transition: none;
}
.ripple.show {
  animation: ripple 0.6s linear;
}
@keyframes ripple {
  to {
    transform: scale(4);
    opacity: 0;
  }
}
td {
  height: 100%;
}
</style>
